import { type FormEvent, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PhotoCapture } from '../components/PhotoCapture';
import { Button } from '../components/ui/Button';
import { TextField } from '../components/ui/TextField';
import { useTrip } from '../hooks/useTrip';
import { useT } from '../i18n';
import { isValidMoneyInput, normalizeMoneyInput } from '../lib/money';
import { enqueueExpense, syncPendingExpenses } from '../lib/sync';

/**
 * Lançamento de gasto — precisa funcionar 100% offline (RF-VIA-04). Grava
 * direto no Dexie e devolve o controle na hora; a sincronização acontece em
 * segundo plano quando (e se) houver rede.
 */
export function NewExpensePage() {
  const { t } = useT();
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const { trip } = useTrip(tripId);
  const [descricao, setDescricao] = useState('');
  const [valor, setValor] = useState('');
  const [fotoDataUrl, setFotoDataUrl] = useState<string>();
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!tripId) return;

    if (descricao.trim().length === 0) {
      setError(t('expenses.errors.descricao'));
      return;
    }
    if (!isValidMoneyInput(valor)) {
      setError(t('expenses.errors.valor'));
      return;
    }

    setSubmitting(true);
    setError(undefined);
    try {
      await enqueueExpense({
        tripId,
        descricao: descricao.trim(),
        valor: normalizeMoneyInput(valor),
        ...(fotoDataUrl ? { fotoDataUrl } : {}),
      });
      // Melhor esforço: tenta sincronizar na hora se já houver rede; não bloqueia a navegação.
      void syncPendingExpenses();
      navigate(`/viagens/${tripId}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('expenses.errors.save'));
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-1 flex-col gap-5 px-4 py-5">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="self-start text-sm text-ink-muted"
      >
        ← {t('common.cancel')}
      </button>

      <div>
        <h1 className="font-display text-xl font-semibold text-ink">{t('expenses.newTitle')}</h1>
        <p className="text-sm text-ink-muted">{t('expenses.newSubtitle')}</p>
      </div>

      <form onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-5">
        <TextField
          label={t('expenses.descricao')}
          placeholder={t('expenses.descricaoPlaceholder')}
          value={descricao}
          onChange={(event) => setDescricao(event.target.value)}
          autoFocus
          required
        />
        <TextField
          label={trip?.moeda ? `${t('expenses.valor')} (${trip.moeda})` : t('expenses.valor')}
          inputMode="decimal"
          placeholder="0,00"
          mono
          value={valor}
          onChange={(event) => setValor(event.target.value)}
          required
        />
        <PhotoCapture value={fotoDataUrl} onChange={setFotoDataUrl} />

        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}

        <Button type="submit" fullWidth loading={submitting}>
          {t('expenses.submit')}
        </Button>
      </form>
    </div>
  );
}
